import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { friendlyError } from "@/lib/errors";
import { initials } from "@/lib/format";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function ProfileSettings() {
  const { user, profile, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [form, setForm] = useState({ full_name: "" });
  const [pw, setPw] = useState({ newPw: "", confirm: "" });

  useEffect(() => {
    if (profile) {
      setForm({ full_name: profile.full_name ?? "" });
    }
  }, [profile]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setProfileMsg("");
    setProfileErr("");
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: form.full_name.trim() || null })
      .eq("id", user!.id);
    setSaving(false);
    if (error) {
      setProfileErr(friendlyError(error.message));
      return;
    }
    setProfileMsg("Profile updated.");
    await refresh();
    queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwMsg("");
    setPwErr("");
    if (pw.newPw.length < 6) {
      setPwErr("New password must be at least 6 characters.");
      return;
    }
    if (pw.newPw !== pw.confirm) {
      setPwErr("Passwords don't match.");
      return;
    }
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw.newPw });
    setPwSaving(false);
    if (error) {
      setPwErr(friendlyError(error.message));
      return;
    }
    setPwMsg("Password updated successfully.");
    setPw({ newPw: "", confirm: "" });
  };

  if (!user) return <LoadingState />;

  const fullName = profile?.full_name || user.email?.split("@")[0] || "User";
  const email = user.email ?? "";

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Update your personal details and password" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Avatar & identity */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xl">
                {initials(fullName)}
              </div>
              <div>
                <p className="text-base font-semibold text-slate-900">{fullName}</p>
                <p className="text-sm text-slate-500">{email}</p>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-slate-800 mb-4">Personal details</h3>
            {profileMsg && (
              <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                {profileMsg}
              </div>
            )}
            {profileErr && (
              <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {profileErr}
              </div>
            )}
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div>
                <label className={labelCls}>Full name</label>
                <input
                  value={form.full_name}
                  onChange={set("full_name")}
                  className={inputCls}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className={labelCls}>Email address</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500 cursor-not-allowed"
                  title="Email cannot be changed here"
                />
                <p className="text-[11px] text-slate-400 mt-1">Contact an administrator to change your email address.</p>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>

          {/* Change password */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Change password</h3>
            {pwMsg && (
              <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                {pwMsg}
              </div>
            )}
            {pwErr && (
              <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {pwErr}
              </div>
            )}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className={labelCls}>New password</label>
                <input
                  type="password"
                  value={pw.newPw}
                  onChange={(e) => setPw((p) => ({ ...p, newPw: e.target.value }))}
                  className={inputCls}
                  placeholder="••••••••"
                  minLength={6}
                />
                <p className="text-[11px] text-slate-400 mt-1">At least 6 characters.</p>
              </div>
              <div>
                <label className={labelCls}>Confirm new password</label>
                <input
                  type="password"
                  value={pw.confirm}
                  onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                  className={inputCls}
                  placeholder="••••••••"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={pwSaving}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  {pwSaving ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>

          {/* Account info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Account information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between py-2 border-b border-slate-50">
                <span className="text-slate-500">User ID</span>
                <span className="font-mono text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded">{user.id}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-50">
                <span className="text-slate-500">Email verified</span>
                <span className={`text-xs font-medium ${user.email_confirmed_at ? "text-emerald-700" : "text-amber-600"}`}>
                  {user.email_confirmed_at ? "Verified" : "Pending verification"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-slate-500">Account created</span>
                <span className="text-slate-600">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <SettingsNav />
      </div>
    </div>
  );
}